
import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useService } from "@/context/ServiceContext";

// Define service types for better type-safety
type ServiceId = 
  | "nutrition-counselling" 
  | "gut-health" 
  | "weight-management" 
  | "sports-nutrition" 
  | "chronic-diseases" 
  | "lifecycle-nutrition";

interface ServiceOption {
  id: ServiceId;
  label: string;
}

const SERVICE_OPTIONS: ServiceOption[] = [
  { id: "nutrition-counselling", label: "Nutrition Counselling" },
  { id: "gut-health", label: "Gut Health" },
  { id: "weight-management", label: "Weight Management" },
  { id: "sports-nutrition", label: "Sports Nutrition" },
  { id: "chronic-diseases", label: "Chronic Diseases" },
  { id: "lifecycle-nutrition", label: "Lifecycle Nutrition" }
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<boolean>(false);
  const { scrollToService, isNavigatingMobile } = useService();
  const navigate = useNavigate();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Close the services dropdown when toggling the main menu
    if (!isMenuOpen) {
      setIsServicesDropdownOpen(false);
      setSelectedService(null);
    }
  };
  
  const toggleServicesDropdown = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsServicesDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 500); // Increased delay for better user experience
  };

  // Handle service selection on mobile - improved for better touch handling
  const handleServiceClick = (serviceId: ServiceId, event: React.MouseEvent | React.TouchEvent) => {
    // Stop propagation to prevent the click from closing menus
    event.preventDefault();
    event.stopPropagation();
    
    // If navigation is already in progress, ignore additional taps
    if (navigationRef.current) {
      console.log("Navigation already in progress, ignoring tap");
      return;
    }
    
    console.log(`Mobile service clicked: ${serviceId}`);
    navigationRef.current = true;
    
    // Highlight the selected service
    setSelectedService(serviceId);
    
    // Use setTimeout to give visual feedback before navigating
    setTimeout(() => {
      try {
        // Navigate to the service using the context's scrollToService
        // which handles both navigation and scrolling
        scrollToService(serviceId);
        
        // Close mobile menus with a delay to ensure navigation starts first
        setTimeout(() => {
          setIsServicesDropdownOpen(false);
          
          // Only close the main menu after a delay
          setTimeout(() => {
            setIsMenuOpen(false);
            navigationRef.current = false;
          }, 500);
        }, 300);
      } catch (error) {
        console.error("Navigation error:", error);
        navigationRef.current = false;
      }
    }, 100);
  };

  // Handle touch events separately for better mobile experience
  const handleServiceTouch = (serviceId: ServiceId, event: React.TouchEvent) => {
    handleServiceClick(serviceId, event);
  };

  // Handle clicks outside of dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        mobileDropdownRef.current && 
        !mobileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when navigating on mobile
  useEffect(() => {
    if (isNavigatingMobile) {
      setIsServicesDropdownOpen(false);
      setIsMenuOpen(false);
    }
  }, [isNavigatingMobile]);
  
  return <header className="absolute top-0 left-0 right-0 z-50">
      <div className="bg-primary py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-white text-sm">
            <div className="text-large font-Large">Medical Aid Rates Apply</div>
            <div>
              <a href="tel:078 351 5631" className="mr-4">📞 078 351 5631</a>
              <a href="mailto:info@functionalfoodie.co.za">✉️ info@functionalfoodie.co.za</a>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/lovable-uploads/24972437-aa8b-4772-a032-7db8f669e478.png" alt="Functional Foodie" className="h-20 w-auto" />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-primary hover:text-secondary transition-colors font-medium">
                Home
              </Link>
              <Link to="/team" className="text-primary hover:text-secondary transition-colors font-medium">
                Our Team
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative group" 
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Link 
                  to="/services" 
                  className="text-primary hover:text-secondary transition-colors font-medium flex items-center"
                  onMouseEnter={handleMouseEnter}
                >
                  Services
                </Link>
                
                {isServicesDropdownOpen && (
                  <div 
                    className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-md py-2 z-[100]"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {SERVICE_OPTIONS.map(service => (
                      <Link 
                        key={service.id}
                        to={`/services/${service.id}`} 
                        className="block px-4 py-2 text-primary hover:bg-gray-100"
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              <Link to="/contact" className="text-primary hover:text-secondary transition-colors font-medium">
                Contact Us
              </Link>
              <Link to="/blog" className="text-primary hover:text-secondary transition-colors font-medium">
                Blog
              </Link>
              <Link to="/booking" className="text-primary hover:text-secondary transition-colors font-medium">
                Book Online
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <Button variant="ghost" className="md:hidden" onClick={toggleMenu} aria-label={isMenuOpen ? "Close menu" : "Open menu"}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </nav>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden bg-white relative z-50">
              <div className="flex flex-col space-y-4 py-4">
                <Link 
                  to="/" 
                  className="text-primary hover:text-secondary transition-colors font-medium" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/team" 
                  className="text-primary hover:text-secondary transition-colors font-medium" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  Our Team
                </Link>
                
                {/* Mobile Services Menu - Enhanced for touch */}
                <div className="flex flex-col relative" ref={mobileDropdownRef}>
                  <button 
                    className="text-primary hover:text-secondary transition-colors font-medium flex justify-between items-center bg-transparent text-left"
                    onClick={toggleServicesDropdown}
                  >
                    Services
                    <span className="ml-2">
                      {isServicesDropdownOpen ? '-' : '+'}
                    </span>
                  </button>
                  
                  {isServicesDropdownOpen && (
                    <div className="flex flex-col pl-4 mt-2 space-y-2 bg-white rounded-md shadow-sm z-[100]">
                      {SERVICE_OPTIONS.map(service => (
                        <div 
                          key={service.id}
                          className={`relative overflow-hidden transition-colors rounded-md ${
                            selectedService === service.id 
                              ? "bg-secondary/10"
                              : ""
                          }`}
                        >
                          <button
                            className={`text-left p-2 w-full block transition-colors ${
                              selectedService === service.id
                                ? "text-secondary font-bold"
                                : "text-primary hover:text-secondary"
                            }`}
                            onClick={(e) => handleServiceClick(service.id, e)}
                            onTouchEnd={(e) => handleServiceTouch(service.id, e)}
                            style={{ touchAction: "manipulation" }}
                          >
                            {service.label}
                          </button>
                          {/* Add clickable overlay with explicit touch handling */}
                          <div 
                            className="absolute inset-0" 
                            style={{ zIndex: 10, touchAction: "manipulation" }}
                            onClick={(e) => handleServiceClick(service.id, e)}
                            onTouchEnd={(e) => handleServiceTouch(service.id, e)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Navigate to ${service.label}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Link 
                  to="/contact" 
                  className="text-primary hover:text-secondary transition-colors font-medium" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact Us
                </Link>
                <Link 
                  to="/blog" 
                  className="text-primary hover:text-secondary transition-colors font-medium" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link 
                  to="/booking" 
                  className="text-primary hover:text-secondary transition-colors font-medium" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  Book Online
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>;
};
